import { useState, useEffect } from "react";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroll-component";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  GET_TABLES_GUESTS_API,
  GET_UNSEATED_GUESTS_API,
  MOVE_GUEST_API,
  GET_SEATING_HEADER_API,
} from "../../Constant/api";
import { LoadingOverlay } from "../../Components/LoadingOverlay";
import Button from "../../Components/Button";
import { useToast } from "../../Components/Toast";
import { useNavigate } from "react-router-dom";
import { SEATS_MANAGEMENT } from "../../Router/path";
import FilterTextInput from "../../Components/FilterTextInput";
import DraggableGuestCard from "../../Components/SeatingChart/DraggableGuestCard";
import NormalMode from "./NormalMode";

const SeatingChart = () => {
  const [loading, setLoading] = useState(false);
  const [unseated, setUnseated] = useState([]);
  const [tables, setTables] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [headerData, setHeaderData] = useState([]);

  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch Header Data
  const fetchHeaderData = async () => {
    setLoading(true);
    try {
      axios
        .get(GET_SEATING_HEADER_API, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("bearer_token")}`,
          },
          params: { eventId: localStorage.getItem("event_id") },
        })
        .then((response) => {
          setLoading(false);
          setHeaderData(response.data.data);
        })
        .catch((error) => {
          setLoading(false);
          toast("Failed to fetch header data.", "error");
          console.error(error);
        });
    } catch (error) {
      setLoading(false);
      toast("Failed to fetch header data.", "error");
      console.error(error);
    }
  };

  // Fetch unseated
  const fetchUnseated = async (pageNum = 1, reset = false) => {
    setLoading(true);
    axios
      .get(GET_UNSEATED_GUESTS_API, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: localStorage.getItem("event_id"),
          perPage: 20,
          page: pageNum,
          search,
        },
      })
      .then((res) => {
        setLoading(false);
        const newData = res.data.data;
        setUnseated((prev) =>
          reset ? newData[0].guest_list : [...prev, ...newData[0].guest_list]
        );
        setHasMore(pageNum < newData[0].pagination.last_page);
      })
      .catch((error) => {
        setLoading(false);
        toast("Failed to fetch unseated guests.", "error");
        console.error(error);
      });
  };

  // Fetch tables
  const fetchTables = async () => {
    setLoading(true);
    axios
      .get(GET_TABLES_GUESTS_API, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bearer_token")}`,
        },
        params: { eventId: localStorage.getItem("event_id") },
      })
      .then((response) => {
        setLoading(false);
        setTables(response.data.data);
      })
      .catch((error) => {
        setLoading(false);
        toast("Failed to fetch table data.", "error");
        console.error(error);
      });
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUnseated(nextPage);
  };

  const onDragEnd = async (result) => {
    setLoading(true);
    const { source, destination, draggableId } = result;
    if (!destination) {
      setLoading(false);
      return;
    }

    const guestId = draggableId;
    const newTableId =
      destination.droppableId === "-2"
        ? null
        : parseInt(destination.droppableId);

    if (newTableId !== null) {
      const destinationTable = tables.find((t) => t.table_id === newTableId);

      if (destinationTable) {
        const currentCount = destinationTable.guest_list.length;
        const capacity = destinationTable.pax;

        if (currentCount >= capacity) {
          toast(
            `Table "${destinationTable.table_name}" is already full.`,
            "error"
          );
          setLoading(false);
          return;
        }
      }
    }

    await axios
      .post(
        MOVE_GUEST_API,
        { guestId, tableId: newTableId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("bearer_token")}`,
          },
        }
      )
      .then(() => {
        setLoading(false);
        toast("Guest moved successfully.", "success");
        fetchUnseated(1, true);
        fetchTables();
      })
      .catch((error) => {
        setLoading(false);
        toast(error.response?.data?.message || "Failed to move guest", "error");
        console.error(error);
      });
  };

  const handleRefresh = () => {
    fetchUnseated(1, true);
    fetchTables();
  };

  useEffect(() => {
    handleRefresh();
  }, [search]);

  useEffect(() => {
    fetchHeaderData();
  }, []);

  return (
    <div className="flex flex-col w-full h-full min-h-screen">
      {loading && <LoadingOverlay />}

      {/* Header */}
      <div className="sticky grid grid-cols-3 w-full p-[24px] h-full max-h-[80px] border border-[#EFF0F2] bg-white items-center justify-between">
        <h1 className="grid text-[20px]">Seating Chart</h1>

        <div className="flex justify-center items-center w-full">
          {headerData && headerData.length > 0 && (
            <div className="flex items-center justify-center gap-[24px]">
              {headerData.map((data, index) => (
                <div className="flex flex-col items-center justify-center">
                  <p className="font-normal text-[#636C7E] text-[12px]">
                    {data.label}
                  </p>
                  <h2 className="font-semibold text-14px">{data.value}</h2>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid w-full items-center justify-end">
          <div className="flex items-center gap-[8px]">
            <Button
              variant="secondary"
              onClick={() => navigate(SEATS_MANAGEMENT)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex h-full w-full">
          {/* Sidebar */}
          <div className="flex flex-col w-full h-[calc(100vh-80px)] max-w-[250px] xl:max-w-[400px] bg-white border-r border-[#EFF0F2] no-scrollbar overflow-y-auto">
            <div className="flex flex-col w-full p-[12px] xl:p-[24px] border-b border-[#EFF0F2] gap-[24px]">
              <div className="flex flex-col w-full text-[14px]">
                <h2>Guest List</h2>
                <p className="font-normal text-[#636C7E]">
                  You can assign seat by drag and drop, or search in the table
                  chart. Able to multi-select drag and swapping seats.
                </p>
              </div>
              <FilterTextInput
                placeholder="Search Guest"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Droppable droppableId="-2">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex flex-col w-full p-[24px] border-t border-[#becbe4] gap-[16px] overflow-auto"
                  id="scrollableUnseated"
                >
                  <InfiniteScroll
                    dataLength={unseated.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={
                      <p className="text-center text-gray-400">Loading...</p>
                    }
                    scrollableTarget="scrollableUnseated"
                  >
                    {unseated.map((guest, index) => (
                      <DraggableGuestCard
                        data={guest}
                        index={index}
                        key={guest.guest_id}
                      />
                    ))}
                  </InfiniteScroll>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* Tables */}
          <div className="flex flex-col w-full h-[calc(100vh-80px)] p-[12px] xl:p-[24px] no-scrollbar overflow-y-auto">
            <NormalMode
              // setMode={setMode}
              tableData={tables}
              handleRefresh={handleRefresh}
            />
          </div>
          {/* <div className="grid grid-cols-3 w-full gap-4 p-4">
            {tables.map((table) => (
              <Droppable
                droppableId={String(table.table_id)}
                key={table.table_id}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="bg-white p-3 rounded-lg shadow-md min-h-[200px]"
                  >
                    <h3 className="font-bold mb-2">{table.table_name}</h3>
                    {table.guest_list.map((guest, index) => (
                      <Draggable
                        key={guest.guest_id}
                        draggableId={String(guest.guest_id)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="p-2 my-1 bg-blue-100 rounded-md"
                          >
                            {guest.salutation ? guest.salutation + ". " : ""}
                            {guest.guest_name}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
          </div> */}
        </div>
      </DragDropContext>
    </div>
  );
};

export default SeatingChart;
